
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useReferralSystem } from "@/hooks/use-referral-system";
import { useReferralTerms } from "@/hooks/use-referral-terms";
import { ReferralCodeGenerator } from "./ReferralCodeGenerator";
import { ReferralAnalytics } from "./ReferralAnalytics";
import { ReferralPayouts } from "./ReferralPayouts";
import { ReferralConversions } from "./ReferralConversions";
import { ReferralTermsDialog } from "./ReferralTermsDialog";
import { PayoutSetupDialog } from "./PayoutSetupDialog";
import { Copy, DollarSign, Users, TrendingUp, Gift, ArrowLeft, Settings, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

export const ReferralDashboard = () => {
  const navigate = useNavigate();
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  
  const { 
    referralCode, 
    analytics, 
    conversions, 
    payouts,
    isLoadingCode,
    isLoadingAnalytics 
  } = useReferralSystem();

  const {
    hasAgreedToTerms,
    payoutPreferences,
    isLoadingTerms,
    isLoadingAgreement,
    isLoadingPrefs
  } = useReferralTerms();

  const copyReferralLink = () => {
    if (referralCode) {
      const link = `${window.location.origin}/?ref=${referralCode.code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  // Calculate total earnings
  const totalEarnings = conversions?.reduce((sum, conv) => 
    conv.status === 'confirmed' ? sum + conv.commission_amount : sum, 0
  ) || 0;

  const pendingEarnings = conversions?.reduce((sum, conv) => 
    conv.status === 'pending' ? sum + conv.commission_amount : sum, 0
  ) || 0;

  const totalClicks = analytics?.reduce((sum, month) => sum + month.total_clicks, 0) || 0;
  const totalConversions = analytics?.reduce((sum, month) => sum + month.total_conversions, 0) || 0;
  const averageConversionRate = totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0;

  // Show terms dialog if user hasn't agreed yet
  const needsTermsAgreement = !isLoadingTerms && !isLoadingAgreement && !hasAgreedToTerms;
  const needsPayoutSetup = !isLoadingPrefs && !payoutPreferences;

  if (needsTermsAgreement) {
    return (
      <>
        <div className="max-w-4xl mx-auto p-6 text-center">
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center justify-center gap-2 text-amber-800">
                <AlertTriangle className="h-6 w-6" />
                Terms Agreement Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-700 mb-4">
                Before you can participate in our referral program, you need to read and agree to the terms and conditions.
              </p>
              <Button onClick={() => setShowTermsDialog(true)}>
                Read Terms & Conditions
              </Button>
            </CardContent>
          </Card>
        </div>
        <ReferralTermsDialog 
          isOpen={showTermsDialog}
          onClose={() => setShowTermsDialog(false)}
        />
      </>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-gray-900">Referral Dashboard</h1>
            <Badge variant="secondary" className="bg-orange-100 text-orange-800">
              BETA
            </Badge>
          </div>
          <p className="text-gray-600">Earn commissions by referring new users to CSO Agent</p>
          <p className="text-sm text-orange-600 font-medium">
            Beta Program - Features and terms may change during development
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPayoutDialog(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Payout Settings
          </Button>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Beta Warning */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <AlertTriangle className="h-5 w-5" />
            Beta Program Notice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-orange-700">
            This referral program is currently in beta. Commission rates, payout methods, and terms may be subject to change. 
            All commissions earned during the beta period will be honored according to the terms at the time they were earned.
          </p>
        </CardContent>
      </Card>

      {/* Setup Warning */}
      {needsPayoutSetup && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <AlertTriangle className="h-5 w-5" />
              Payout Setup Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-orange-700 mb-4">
              Please set up your payout preferences to receive commission payments.
            </p>
            <Button onClick={() => setShowPayoutDialog(true)}>
              Set Up Payout Method
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Commission Structure */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Commission Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="font-semibold text-blue-600">Monthly Plan</div>
              <div className="text-2xl font-bold text-blue-800">$5</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="font-semibold text-green-600">Team Plan</div>
              <div className="text-2xl font-bold text-green-800">$20</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="font-semibold text-purple-600">Enterprise Plan</div>
              <div className="text-2xl font-bold text-purple-800">$80</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="font-semibold text-yellow-600">Lifetime Plan</div>
              <div className="text-2xl font-bold text-yellow-800">$175</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">${totalEarnings.toFixed(2)}</div>
            {pendingEarnings > 0 && (
              <p className="text-xs text-muted-foreground">
                ${pendingEarnings.toFixed(2)} pending
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalClicks}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalConversions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageConversionRate.toFixed(1)}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link */}
      {referralCode && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Your Referral Link</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <code className="flex-1 p-3 bg-gray-100 rounded border">
                {window.location.origin}/?ref={referralCode.code}
              </code>
              <Button onClick={copyReferralLink} variant="outline" size="icon">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="conversions">Conversions</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <ReferralCodeGenerator />
        </TabsContent>

        <TabsContent value="analytics">
          <ReferralAnalytics />
        </TabsContent>

        <TabsContent value="conversions">
          <ReferralConversions />
        </TabsContent>

        <TabsContent value="payouts">
          <ReferralPayouts />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ReferralTermsDialog 
        isOpen={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />
      <PayoutSetupDialog 
        isOpen={showPayoutDialog}
        onClose={() => setShowPayoutDialog(false)}
      />
    </div>
  );
};
